import { useCallback, useMemo } from 'react'
import { useDashboardFilters } from '@/components/growth/useDashboardFilters'
import { previousRangeFor, rangeParamsFor } from '@/components/growth/dashboardFilters'
import { channelApiParams } from './affiliateLinksFormat'

const SOURCES = new Set(['marketing', 'user'])
const STATUSES = new Set(['active', 'expired'])
const DEVICES = new Set(['ios', 'android', 'desktop', 'other'])
const CHANNELS = new Set(['link', 'whatsapp', 'x', 'facebook', 'telegram', 'email', 'native', 'image', 'direct', 'code'])

// What the table can group by. Sharers are players, so they exist only for
// player links; affiliates only for marketing links; one affiliate's links
// are just links.
export function viewsFor(source, affiliate) {
  if (affiliate) return ['links']
  if (source === 'user') return ['links', 'sharers']
  if (source === 'marketing') return ['links', 'affiliates']
  return ['links', 'sharers', 'affiliates']
}

// Everything the Live links page shows lives in the URL, so a link carries
// it. The range, comparison and custom dates are Growth's own parameters
// (range, from, to, compare); the rest are this page's. Source and affiliate
// narrow the whole page; view, status and search only the table.
export function useLiveLinksFilters() {
  const { filters, changeFilters, updateParams, searchParams } = useDashboardFilters()
  // The range is resolved again only when the range itself changes. Growth's
  // filters resolve it on every URL change, and this page writes the URL for
  // its table too: paging or switching the table must not move "now" and
  // reload every widget. This page has no states, so none are carried.
  const { preset, customStart, customEnd } = filters
  const range = useMemo(() => {
    const resolved = rangeParamsFor({ preset, customStart, customEnd })
    return resolved ? { start_at: resolved.start_at, end_at: resolved.end_at } : null
  }, [preset, customStart, customEnd])

  const value = useMemo(() => {
    const source = SOURCES.has(searchParams.get('source')) ? searchParams.get('source') : ''
    // Player links have no affiliate, so that filter stops applying.
    const affiliate = source === 'user' ? '' : (searchParams.get('affiliate') || '')
    const views = viewsFor(source, affiliate)
    const view = views.includes(searchParams.get('view')) ? searchParams.get('view') : 'links'
    const status = STATUSES.has(searchParams.get('status')) ? searchParams.get('status') : ''
    const search = (searchParams.get('q') || '').trim()
    const device = DEVICES.has(searchParams.get('device')) ? searchParams.get('device') : ''
    const channel = CHANNELS.has(searchParams.get('channel')) ? searchParams.get('channel') : ''
    const sport = (searchParams.get('sport') || '').trim().slice(0, 64)
    const pageParams = range ? {
      ...range,
      ...(source ? { source } : {}),
      ...(affiliate ? { affiliate_id: affiliate } : {}),
      ...(device ? { device } : {}),
      ...channelApiParams(channel),
      ...(sport ? { sport } : {}),
    } : null
    const previousRange = filters.compare && range ? previousRangeFor(range) : null
    return {
      filters,
      range,
      previousParams: previousRange && pageParams ? { ...pageParams, ...previousRange } : null,
      pageParams,
      source,
      affiliate,
      views,
      view,
      status,
      search,
      device,
      channel,
      sport,
      currencyParam: searchParams.get('currency') || '',
    }
  }, [filters, range, searchParams])

  const setParams = useCallback((patch) => {
    updateParams((next) => {
      for (const [key, entry] of Object.entries(patch)) {
        if (entry) next.set(key, entry)
        else next.delete(key)
      }
    })
  }, [updateParams])

  return { ...value, changeFilters, setParams }
}
